import { useMemo } from "react";
import type { ComplianceSummary, Department, HeatmapRow } from "../types";
import { windowLong, windowShort } from "../lib/reportingWindow";

interface Props {
  summary: ComplianceSummary;
}

const TARGET = 80;

type PillarKey = "weekly" | "month_end" | "approval" | "hours";

const PILLARS: { key: PillarKey; short: string; long: string; unit: string }[] = [
  { key: "weekly", short: "Wk", long: "Weekly submission", unit: "person-weeks submitted on time" },
  { key: "month_end", short: "ME", long: "Month-end submission", unit: "person-months closed by month-end" },
  { key: "approval", short: "Appr", long: "Team-lead approval", unit: "person-months approved on time" },
  { key: "hours", short: "Hrs", long: "Hours worked", unit: "person-weeks with allotted hours logged" },
];

interface PillarStat {
  passed: number;
  evaluated: number;
  pct: number | null;
}

type PillarStats = Record<PillarKey, PillarStat>;

function severityColors(pct: number | null): {
  bg: string;
  border: string;
  ring: string;
  text: string;
  chipBg: string;
  chipText: string;
} {
  if (pct === null) {
    return {
      bg: "#F8FAFC",
      border: "#CBD5E1",
      ring: "#94A3B8",
      text: "#475569",
      chipBg: "#E2E8F0",
      chipText: "#475569",
    };
  }
  if (pct >= TARGET) {
    return {
      bg: "#ECFDF5",
      border: "#10B981",
      ring: "#10B981",
      text: "#065F46",
      chipBg: "#D1FAE5",
      chipText: "#065F46",
    };
  }
  if (pct >= TARGET - 10) {
    return {
      bg: "#FFFBEB",
      border: "#F59E0B",
      ring: "#F59E0B",
      text: "#92400E",
      chipBg: "#FEF3C7",
      chipText: "#92400E",
    };
  }
  return {
    bg: "#FEF2F2",
    border: "#EF4444",
    ring: "#EF4444",
    text: "#991B1B",
    chipBg: "#FEE2E2",
    chipText: "#991B1B",
  };
}

// Round to the precision we display BEFORE choosing a color, so a value
// shown as "80%" can never be colored as below target (79.6 -> 80).
function round0(pct: number | null): number | null {
  return pct === null ? null : Math.round(pct);
}
function round1(pct: number | null): number | null {
  return pct === null ? null : Math.round(pct * 10) / 10;
}

function ymOf(iso: string): string {
  return iso.slice(0, 7);
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

/**
 * Per-department pillar rates, computed from the heatmap cells the
 * summary endpoint already ships. The rules mirror
 * _build_monthly_compliance in fulfillment_dashboard/src/compliance.py
 * (the source of the "Compliance trend" since-window numbers), so the
 * department figures use the same definitions as the company trend:
 *   - only completed months listed in summary.monthly_compliance count
 *   - weekly: weeks whose Saturday falls in a counted month; no_entries
 *     and pending weeks excluded
 *   - hours: same weeks; cells with hours_pass === null excluded
 *   - month-end: the month's spanning_week cell; null excluded
 *   - approval: one cell per month (backend paints the per-person-month
 *     result on every week starting in that month); months whose
 *     approval deadline hasn't passed (approval_pct null) are skipped
 * Department membership comes from row.department, which the backend
 * assigns with the same reporting-subtree walk used for the tiles.
 */
function computePillarStats(summary: ComplianceSummary): Map<string, PillarStats> {
  const weeks = summary.heatmap?.weeks ?? [];
  const months = summary.monthly_compliance ?? [];
  const countedMonths = new Set(months.map((m) => m.month));

  const weeklyIdx: number[] = [];
  weeks.forEach((w, i) => {
    if (countedMonths.has(ymOf(addDaysIso(w, 6)))) weeklyIdx.push(i);
  });

  const weekIdx = new Map(weeks.map((w, i) => [w, i] as const));
  const monthEndIdx: number[] = [];
  for (const m of months) {
    if (!m.spanning_week) continue;
    const idx = weekIdx.get(m.spanning_week);
    if (idx !== undefined) monthEndIdx.push(idx);
  }

  const approvalIdx: number[] = [];
  for (const m of months) {
    if (m.approval_pct === null || m.approval_pct === undefined) continue;
    const idx = weeks.findIndex((w) => ymOf(w) === m.month);
    if (idx >= 0) approvalIdx.push(idx);
  }

  const empty = (): PillarStats => ({
    weekly: { passed: 0, evaluated: 0, pct: null },
    month_end: { passed: 0, evaluated: 0, pct: null },
    approval: { passed: 0, evaluated: 0, pct: null },
    hours: { passed: 0, evaluated: 0, pct: null },
  });

  const out = new Map<string, PillarStats>();
  const rows: HeatmapRow[] = summary.heatmap?.rows ?? [];
  for (const r of rows) {
    const key = r.department || "Other";
    let s = out.get(key);
    if (!s) {
      s = empty();
      out.set(key, s);
    }
    for (const i of weeklyIdx) {
      const c = r.values[i];
      if (!c) continue;
      if (c.hours_pass !== null && c.hours_pass !== undefined) {
        s.hours.evaluated += 1;
        if (c.hours_pass) s.hours.passed += 1;
      }
      if (c.label === "no_entries" || c.label === "pending") continue;
      s.weekly.evaluated += 1;
      if (c.label === "on_time") s.weekly.passed += 1;
    }
    for (const i of monthEndIdx) {
      const c = r.values[i];
      if (!c || c.month_end_pass === null || c.month_end_pass === undefined) continue;
      s.month_end.evaluated += 1;
      if (c.month_end_pass) s.month_end.passed += 1;
    }
    for (const i of approvalIdx) {
      const c = r.values[i];
      if (!c || c.approval_pass === null || c.approval_pass === undefined) continue;
      s.approval.evaluated += 1;
      if (c.approval_pass) s.approval.passed += 1;
    }
  }

  for (const s of out.values()) {
    for (const p of PILLARS) {
      const st = s[p.key];
      st.pct = st.evaluated > 0 ? (100 * st.passed) / st.evaluated : null;
    }
  }
  return out;
}

function pillarAverage(stats: PillarStats | undefined): number | null {
  if (!stats) return null;
  const vals = PILLARS.map((p) => stats[p.key].pct).filter(
    (v): v is number => v !== null,
  );
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function PillarBar({ label, title, pct }: { label: string; title: string; pct: number | null }) {
  const shown = round0(pct);
  const c = severityColors(shown);
  return (
    <div
      className="grid items-center gap-1.5 text-[10.5px]"
      style={{ gridTemplateColumns: "34px 1fr 32px", color: "#475569" }}
      title={title}
    >
      <span>{label}</span>
      <div className="relative h-[7px] rounded" style={{ background: "#E2E8F0" }}>
        <div
          className="h-full rounded"
          style={{ width: `${shown ?? 0}%`, background: c.ring }}
        />
        <div
          className="absolute"
          style={{
            left: `${TARGET}%`,
            top: -3,
            bottom: -3,
            borderLeft: "1.5px dashed #64748B",
          }}
        />
      </div>
      <span className="text-right font-bold tabular-nums" style={{ color: c.ring }}>
        {shown === null ? "-" : `${shown}%`}
      </span>
    </div>
  );
}

function DepartmentTile({
  dept,
  stats,
  windowLabel,
}: {
  dept: Department;
  stats: PillarStats | undefined;
  windowLabel: string;
}) {
  const avg = round1(pillarAverage(stats));
  const c = severityColors(avg);
  const avgText = avg === null ? "n/a" : `${avg.toFixed(1)}%`;
  const cleanText = dept.ytd_pct === null ? "n/a" : `${dept.ytd_pct.toFixed(1)}%`;

  const pillarLines = PILLARS.map((p) => {
    const st = stats?.[p.key];
    if (!st || st.pct === null) return `${p.long}: not evaluable`;
    return `${p.long}: ${st.pct.toFixed(1)}% (${st.passed}/${st.evaluated} ${p.unit})`;
  }).join("\n");

  return (
    <div
      className="rounded-2xl border-2 p-4 flex flex-col gap-1"
      style={{ background: c.bg, borderColor: c.border }}
      title={
        dept.missing_lead
          ? `${dept.lead_name} not found on the active roster`
          : `${dept.name}\n` +
            `Lead: ${dept.lead_name}\n` +
            `Pillar average ${windowLabel}: ${avgText}\n` +
            pillarLines +
            "\n" +
            `Clean weeks (all 4 pillars in the same week): ${cleanText} (${dept.ytd_weeks_compliant}/${dept.ytd_weeks_evaluated ?? dept.ytd_weeks_total} person-weeks)\n` +
            `People in dept: ${dept.employee_count}`
      }
    >
      <div
        className="text-[10.5px] font-bold uppercase tracking-wider"
        style={{ color: c.text }}
      >
        {dept.name}
      </div>
      <div className="text-[11px]" style={{ color: "#475569" }}>
        Led by {dept.lead_name}
      </div>
      <div
        className="text-3xl font-extrabold tabular-nums mt-1"
        style={{ color: c.ring }}
      >
        {avgText}
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-1">
        <span
          className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: c.chipBg, color: c.chipText }}
          title={`${dept.employee_count} ${dept.employee_count === 1 ? "person" : "people"} in the reporting subtree`}
        >
          {dept.employee_count} {dept.employee_count === 1 ? "person" : "people"}
        </span>
        {dept.missing_lead && (
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "#FEE2E2", color: "#991B1B" }}
          >
            lead not found
          </span>
        )}
      </div>
      {!dept.missing_lead && (
        <>
          <div className="flex flex-col gap-[5px] mt-2">
            {PILLARS.map((p) => {
              const st = stats?.[p.key];
              return (
                <PillarBar
                  key={p.key}
                  label={p.short}
                  pct={st?.pct ?? null}
                  title={
                    !st || st.pct === null
                      ? `${p.long}: not evaluable`
                      : `${p.long}: ${st.pct.toFixed(1)}% (${st.passed}/${st.evaluated} ${p.unit}) - target ${TARGET}%`
                  }
                />
              );
            })}
          </div>
          <div
            className="text-[10.5px] mt-2 pt-1.5"
            style={{ color: "#475569", borderTop: "1px dashed #CBD5E1" }}
            title="Share of person-weeks that passed all four pillars in the same week"
          >
            Clean weeks (all 4 pillars): {cleanText}
          </div>
        </>
      )}
    </div>
  );
}

export default function DepartmentTiles({ summary }: Props) {
  const depts = summary.departments ?? [];
  const statsByDept = useMemo(() => computePillarStats(summary), [summary]);
  if (depts.length === 0) return null;

  return (
    <section className="rounded-2xl bg-white shadow-card border border-slate-200/70 p-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <h2
            className="text-base font-bold tracking-tight"
            style={{ color: "#111827" }}
          >
            Compliance by department · {windowShort(summary)}
          </h2>
          <p className="text-xs mt-0.5" style={{ color: "#475569" }}>
            Each tile rolls up the lead's full reporting subtree {windowLong(summary)}. The big number is the average of the four pillar rates - weekly submission, month-end, approval, and hours worked - using the same definitions as the Compliance trend below. Bars show each pillar against the {TARGET}% target. "Clean weeks" is the stricter share of person-weeks that passed all four pillars at once.
          </p>
        </div>
      </div>
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {depts.map((d) => (
          <DepartmentTile
            key={d.name}
            dept={d}
            stats={statsByDept.get(d.name)}
            windowLabel={windowLong(summary)}
          />
        ))}
      </div>
    </section>
  );
}
